import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import { IcoSun, IcoMoon, IcoUsers, IcoCheck, IcoX } from '../dashboard/DashIcons';

function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem('ce-theme') === 'dark');
  const toggle = () => setDark(d => {
    const next = !d;
    localStorage.setItem('ce-theme', next ? 'dark' : 'light');
    return next;
  });
  return [dark, toggle];
}

const PLANS = [
  {
    key:      'starter',
    name:     'Starter',
    price:    '$29',
    period:   '/mo',
    tagline:  'Everything you need to run basic payroll',
    employees:'Up to 30 employees',
    color:    '#22c55e',
    features: [
      'Clock in / clock out',
      'Basic payroll processing',
      'Time tracking',
      'Scheduling & shift management',
      'PTO tracking',
      'Mobile app access',
      'ADP-compatible export',
      'Live chat support',
      'Email support',
    ],
    notIncluded: ['Reporting & analytics', 'EVV compliance', 'Integrations'],
  },
  {
    key:      'professional',
    name:     'Professional',
    price:    '$69',
    period:   '/mo',
    tagline:  'Built for home health agencies with complex pay rules',
    employees:'Up to 100 employees',
    color:    '#8b5cf6',
    popular:  true,
    features: [
      'Everything in Starter',
      'Mileage reimbursement',
      'Split shifts',
      'Live-in rates',
      'EVV compliance',
      'Reporting & analytics',
      'Priority support',
    ],
    notIncluded: ['Integrations'],
  },
  {
    key:      'growth',
    name:     'Growth',
    price:    '$149',
    period:   '/mo',
    tagline:  'For growing agencies ready to scale operations',
    employees:'Up to 150 employees',
    color:    '#0ea5e9',
    features: [
      'Everything in Professional',
      'Larger employee capacity',
      'Dedicated onboarding support',
    ],
    notIncluded: ['Custom integrations'],
  },
  {
    key:      'enterprise',
    name:     'Enterprise',
    price:    'Custom',
    period:   '',
    tagline:  'Custom pricing for large or multi-location agencies',
    employees:'Unlimited employees',
    color:    '#22c55e',
    features: [
      'Everything in Growth',
      'Integrations',
      'Dedicated account manager',
      'SLA guarantee',
      'White-glove onboarding',
      'Phone support',
    ],
    notIncluded: [],
  },
];

const FAQS = [
  { q: 'Is there a free trial?',           a: 'Yes — every new account gets a 14-day free trial with full access to all features on your chosen plan. No credit card required to start.' },
  { q: 'Can I change plans later?',         a: 'Absolutely. You can upgrade or downgrade at any time from your account settings. Changes take effect immediately.' },
  { q: 'Is my payroll data secure?',        a: 'Yes. All data is encrypted in transit and at rest. We use JWT authentication, rate limiting, and account lockout protection.' },
  { q: 'Does it work with ADP?',            a: 'ClockEarnings exports payroll in ADP-compatible CSV format so you can import directly into ADP without manual data entry.' },
  { q: 'Are the prices flat-rate?',         a: 'Yes — all plans are flat monthly fees. There are no per-seat or per-employee charges. Your price stays the same regardless of how many employees are active.' },
  { q: 'What happens after the trial?',     a: 'After 14 days, your account moves to the plan you selected. We\'ll send a reminder before the trial ends so you\'re never caught off guard.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [dark, toggleDark] = useDarkMode();
  const [openFaq, setOpenFaq] = useState(null);

  const T = dark ? {
    bg:           '#0d1b2a',
    bgCard:       'rgba(255,255,255,0.03)',
    bgCardBorder: 'rgba(34,197,94,0.1)',
    bgNav:        'rgba(10,15,30,0.95)',
    navBorder:    'rgba(34,197,94,0.08)',
    heading:      '#d1fae5',
    text:         '#e2e8f0',
    textMuted:    'rgba(148,163,184,0.8)',
    textFaint:    'rgba(100,116,139,0.6)',
    divider:      'rgba(34,197,94,0.08)',
    navLink:      '#94a3b8',
    toggleBg:     'rgba(255,255,255,0.08)',
    toggleColor:  '#94a3b8',
    faqQ:         '#e2e8f0',
    faqA:         'rgba(148,163,184,0.8)',
    featureText:  'rgba(226,232,240,0.85)',
    footerText:   'rgba(100,116,139,0.6)',
    popularCard:  'rgba(139,92,246,0.06)',
    popularBorder:'rgba(139,92,246,0.3)',
  } : {
    bg:           '#f8fafc',
    bgCard:       '#ffffff',
    bgCardBorder: '#e2e8f0',
    bgNav:        'rgba(255,255,255,0.97)',
    navBorder:    '#e2e8f0',
    heading:      '#0f172a',
    text:         '#1e293b',
    textMuted:    '#64748b',
    textFaint:    '#94a3b8',
    divider:      '#e2e8f0',
    navLink:      '#475569',
    toggleBg:     '#f1f5f9',
    toggleColor:  '#475569',
    faqQ:         '#0f172a',
    faqA:         '#64748b',
    featureText:  '#374151',
    footerText:   '#94a3b8',
    popularCard:  '#f5f3ff',
    popularBorder:'#c4b5fd',
  };

  return (
    <div style={{ minHeight:'100vh', background: T.bg, fontFamily:"'DM Sans','Segoe UI',sans-serif", color: T.text, overflowX:'hidden', transition:'background 0.2s, color 0.2s' }}>
      <style>{`* { box-sizing:border-box; }
        @media(max-width:640px){
          .plans-grid { grid-template-columns:1fr !important; }
          .nav-btns { gap:8px !important; }
          .nav-btns button { padding:8px 12px !important; font-size:13px !important; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 40px', borderBottom:`1px solid ${T.navBorder}`, background: T.bgNav, backdropFilter:'blur(12px)', position:'sticky', top:0, zIndex:100 }}>
        <img src={logo} alt="ClockEarnings" onClick={() => navigate('/')}
          style={{ height:36, cursor:'pointer', filter: 'none' }} />
        <div className="nav-btns" style={{ display:'flex', gap:10, alignItems:'center' }}>
          <button onClick={() => navigate('/login')}
            style={{ padding:'8px 18px', background:'transparent', border:`1px solid ${T.bgCardBorder}`, borderRadius:8, color: T.navLink, fontSize:14, fontWeight:600, cursor:'pointer' }}>
            Sign in
          </button>
          <button onClick={() => navigate('/register')}
            style={{ padding:'9px 18px', background:'linear-gradient(135deg,#16a34a,#22c55e)', border:'none', borderRadius:8, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer' }}>
            Start free trial
          </button>
          <button onClick={toggleDark} title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ padding:'8px 10px', background: T.toggleBg, border:`1px solid ${T.bgCardBorder}`, borderRadius:8, color: T.toggleColor, fontSize:16, cursor:'pointer', lineHeight:1 }}>
            {dark ? <IcoSun size={16}/> : <IcoMoon size={16}/>}
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div style={{ textAlign:'center', padding:'72px 24px 56px', position:'relative', overflow:'hidden' }}>
        {dark && <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:600, height:300, background:'radial-gradient(ellipse,rgba(139,92,246,0.12) 0%,transparent 70%)', pointerEvents:'none' }} />}
        <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 16px', background: dark ? 'rgba(139,92,246,0.1)' : '#f5f3ff', border:`1px solid ${dark ? 'rgba(139,92,246,0.25)' : '#c4b5fd'}`, borderRadius:100, fontSize:12, color: dark ? '#a78bfa' : '#7c3aed', fontWeight:600, letterSpacing:'.05em', marginBottom:24 }}>
          Simple, transparent pricing
        </div>
        <h1 style={{ fontSize:'clamp(30px,6vw,52px)', fontWeight:900, color: T.heading, letterSpacing:'-1.5px', lineHeight:1.1, maxWidth:680, margin:'0 auto 16px' }}>
          Flat monthly pricing.<br />No per-seat surprises.
        </h1>
        <p style={{ fontSize:'clamp(15px,2.5vw,18px)', color: T.textMuted, maxWidth:500, margin:'0 auto 32px', lineHeight:1.65 }}>
          Every plan is a flat monthly rate — no per-employee charges, no hidden fees. Pick the size that fits your agency and scale when you're ready.
        </p>
        <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background: dark ? 'rgba(16,185,129,0.08)' : '#f0fdf4', border:`1px solid ${dark ? 'rgba(16,185,129,0.2)' : '#bbf7d0'}`, borderRadius:100, fontSize:13, color: dark ? '#6ee7b7' : '#15803d', fontWeight:600 }}>
          14-day free trial — no credit card required
        </div>
      </div>

      {/* ── Plans ── */}
      <div className="plans-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:20, maxWidth:1160, margin:'0 auto', padding:'0 24px 80px' }}>
        {PLANS.map(plan => (
          <div key={plan.key} style={{
            background:    plan.popular ? T.popularCard   : T.bgCard,
            border:        `1px solid ${plan.popular ? T.popularBorder : T.bgCardBorder}`,
            borderRadius:  20, padding:'32px 26px', position:'relative',
            transform:     plan.popular ? 'scale(1.02)' : 'none',
            boxShadow:     plan.popular ? (dark ? '0 0 40px rgba(139,92,246,0.15)' : '0 4px 24px rgba(139,92,246,0.12)') : (dark ? 'none' : '0 1px 4px rgba(0,0,0,0.05)'),
          }}>
            {plan.popular && (
              <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background:'linear-gradient(135deg,#8b5cf6,#6d28d9)', color:'#fff', fontSize:11, fontWeight:700, padding:'4px 14px', borderRadius:100, letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                ⭐ MOST POPULAR
              </div>
            )}

            <div style={{ fontSize:13, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color: plan.color, marginBottom:8 }}>
              {plan.name}
            </div>

            <div style={{ display:'flex', alignItems:'baseline', gap:2, marginBottom:4 }}>
              <span style={{ fontSize: plan.price === 'Custom' ? 34 : 44, fontWeight:900, color: T.heading, letterSpacing:'-1px', lineHeight:1 }}>
                {plan.price}
              </span>
              {plan.period && <span style={{ fontSize:14, color: T.textMuted, fontWeight:500 }}>{plan.period}</span>}
            </div>

            <div style={{ fontSize:13, color: T.textFaint, marginBottom:6 }}>
              {plan.price === 'Custom' ? 'Contact us for a quote' : 'flat rate — no per-seat fees'}
            </div>

            <div style={{ fontSize:13, color: T.textMuted, marginBottom:20, paddingBottom:20, borderBottom:`1px solid ${T.divider}` }}>
              <><IcoUsers size={14}/> {plan.employees}</>
            </div>

            <ul style={{ listStyle:'none', padding:0, margin:'0 0 28px', display:'flex', flexDirection:'column', gap:10 }}>
              {plan.features.map(f => (
                <li key={f} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13.5, color: T.featureText }}>
                  <span style={{ width:18, height:18, borderRadius:'50%', background: dark ? 'rgba(16,185,129,0.15)' : '#dcfce7', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><IcoCheck size={10} color={dark ? '#6ee7b7' : '#16a34a'}/></span>
                  {f}
                </li>
              ))}
              {plan.notIncluded.map(f => (
                <li key={f} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13.5, color: T.featureText, opacity:0.35 }}>
                  <span style={{ width:18, height:18, borderRadius:'50%', background: dark ? 'rgba(100,116,139,0.1)' : '#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><IcoX size={10} color="#94a3b8"/></span>
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => plan.key === 'enterprise'
                ? (window.location.href = 'mailto:contact@flurrysystems.com?subject=Enterprise Plan Inquiry')
                : navigate('/register', { state: { plan: plan.key } })
              }
              style={{
                width:'100%', padding:'13px', border:'none', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', letterSpacing:'.02em',
                background: plan.popular ? 'linear-gradient(135deg,#8b5cf6,#6d28d9)' : (plan.key === 'enterprise' ? 'linear-gradient(135deg,#16a34a,#22c55e)' : `${plan.color}18`),
                color:      plan.popular || plan.key === 'enterprise' ? '#fff' : plan.color,
                border:     plan.popular || plan.key === 'enterprise' ? 'none' : `1px solid ${plan.color}44`,
                boxShadow:  plan.popular ? '0 4px 20px rgba(139,92,246,0.3)' : 'none',
              }}
            >
              {plan.key === 'enterprise' ? 'Contact us' : `Get started with ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* ── Feature comparison note ── */}
      <div style={{ textAlign:'center', maxWidth:640, margin:'0 auto', padding:'0 24px 64px', color: T.textMuted, fontSize:14, lineHeight:1.7 }}>
        All plans include: clock in/out, basic payroll, time tracking, scheduling, PTO tracking, mobile app access, ADP export, and live chat + email support.
      </div>

      {/* ── FAQ ── */}
      <div style={{ maxWidth:680, margin:'0 auto', padding:'0 24px 80px' }}>
        <h2 style={{ fontSize:'clamp(24px,4vw,32px)', fontWeight:800, color: T.heading, textAlign:'center', marginBottom:40, letterSpacing:'-0.5px' }}>
          Frequently asked questions
        </h2>
        {FAQS.map((faq, i) => (
          <div key={i} style={{ borderBottom:`1px solid ${T.divider}`, padding:'20px 0' }}>
            <div
              style={{ fontSize:15, fontWeight:600, color: T.faqQ, marginBottom: openFaq === i ? 10 : 0, cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16 }}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <span>{faq.q}</span>
              <span style={{ color: T.textFaint, fontSize:20, flexShrink:0 }}>{openFaq === i ? '−' : '+'}</span>
            </div>
            {openFaq === i && <div style={{ fontSize:14, color: T.faqA, lineHeight:1.75 }}>{faq.a}</div>}
          </div>
        ))}
      </div>

      {/* ── CTA ── */}
      <div style={{ textAlign:'center', padding:'0 24px 80px' }}>
        <div style={{ background: dark ? 'rgba(255,255,255,0.02)' : '#f8fafc', border:`1px solid ${T.bgCardBorder}`, borderRadius:20, padding:'48px 32px', maxWidth:600, margin:'0 auto' }}>
          <h2 style={{ fontSize:'clamp(22px,4vw,32px)', fontWeight:800, color: T.heading, marginBottom:12, letterSpacing:'-0.5px' }}>
            Not sure which plan is right?
          </h2>
          <p style={{ color: T.textMuted, fontSize:15, marginBottom:28, lineHeight:1.6 }}>
            Start with a 14-day free trial on any plan. You can switch plans during or after your trial with no penalty.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ padding:'13px 28px', background:'linear-gradient(135deg,#16a34a,#22c55e)', border:'none', borderRadius:12, color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', boxShadow:'0 6px 24px rgba(34,197,94,0.3)' }}>
              Start free trial
            </button>
            <button onClick={() => window.location.href = 'mailto:contact@flurrysystems.com'}
              style={{ padding:'13px 28px', background:'transparent', border:`1px solid ${T.bgCardBorder}`, borderRadius:12, color: T.navLink, fontSize:15, fontWeight:600, cursor:'pointer' }}>
              Contact us
            </button>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ borderTop:`1px solid ${T.divider}`, padding:'28px 40px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:16 }}>
        <img src={logo} alt="ClockEarnings" onClick={() => navigate('/')}
          style={{ height:28, cursor:'pointer', filter: 'none' }} />
        <div style={{ display:'flex', gap:20 }}>
          <button onClick={() => navigate('/')} style={{ background:'none', border:'none', color: T.footerText, fontSize:13, cursor:'pointer', padding:0 }}>Home</button>
          <a href="mailto:contact@flurrysystems.com" style={{ color: T.footerText, fontSize:13, textDecoration:'none' }}>Contact</a>
        </div>
        <span style={{ fontSize:12, color: T.footerText }}>© {new Date().getFullYear()} ClockEarnings</span>
      </footer>
    </div>
  );
}
